import { API, Tag } from "@brg/api";
import { CommonInput, FlexBox, INPUT_TYPES, useToasts } from "@brg/ui";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { TagAdminSelector } from "shared/reducers/tagAdminSlice";
import { AddButton, ColumnContainer, CreateNewCatalogContainer, EditButtonsWrapper, EditTextarea, EditTitleContainer, NewCategoryButtonsContainer, SmallLabel } from "../styles";
import { ProjectAddButton } from "shared/pages/projectEdit/ProjectAddButton";
import { Input } from "@brg/input";
import { BTag } from "core/components";
import { bTag, useModal } from "core";
import { LangObject } from "@brg/api/dist/api-core/utils/lang-utils";
import { setTextByLang } from "shared/pages/teams/TeamEdit";
import { createDefaultLangWithLocal, getLangTitle } from "../MainTagAdminPage";
import { TAGS_LEVEL } from "shared/modals/selectTagsModal/FoundedChildrens";
import {BTagsGridNew} from "../../../../../core/components/bTag/bTagsGridNew/bTagsGridNew";
import { ADMIN_TAGS_TYPE } from "../types";

//при стандартном значение lineHeight режутся некоторые буквы
const ADDITIONAL_HEIGHT_PART = 3;

export interface CurrentLangT extends LangObject {
    currentLang: string;
}

interface InewTagProps {
    tag: Tag<'Local'>,
    groupId: string
}

export enum SUB_TYPES {
    sub = 'sub',
    add = 'add'
}

const autoResize = (area: HTMLTextAreaElement) => {
    const lineHeight = window.getComputedStyle(area as HTMLElement, null).getPropertyValue('line-height');
    area.style.height = 'auto';
    area.rows = 1
    const secondLine = area.scrollHeight >= (parseInt(lineHeight) * 2);
  
    if (secondLine) {
      area.rows = 2
      area.style.height = area.scrollHeight + 'px';
    } else {
      area.style.height = `${parseInt(lineHeight) + ADDITIONAL_HEIGHT_PART}px`;
    }
  };

export const CategoryEdit: FC<{
    onChildrenRemove: (ev: any) => void;
    onChildrenAdd: (tags: Tag<'Local'>[]) => void;
    onCreateSubCategory: (name: CurrentLangT[]) => void;
    onCreateNewTag: ({ tag, groupId}: InewTagProps) => void;
    onCategoryRename: (tag: Tag<'Global'>) => void;
    onLocalLangChange: (arr: any[], lang: string, callback: () => any[]) => void;
    update?: number;
    onTagEdit?: (tagData: any) => void
}> = ({
    onChildrenRemove,
    onChildrenAdd,
    onCategoryRename,
    onCreateSubCategory,
    onCreateNewTag,
    onLocalLangChange,
    update,
    onTagEdit
}) => {
    const { t, i18n } = useTranslation();
    const toast = useToasts();
    const modal = useModal();
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const { isEditMode, currentPlatform, isLoading, type: tagType } = useSelector(TagAdminSelector);
    const [isEditTitle, setIsEditTitle ] = useState(false);
    const [time, setTime] = useState(Date.now())
    const [title, setTitle] = useState<CurrentLangT[]>(createDefaultLangWithLocal());
    const [subTitle, setSubTitle] = useState<CurrentLangT[]>(createDefaultLangWithLocal());
    const [type, setType] = useState<SUB_TYPES | null>();

    const computeTag = useMemo(
        //@ts-ignore
        () => currentPlatform?.editCategory?.mainTag as Tag<'Global'>
    , [currentPlatform, update, title]) as Tag<'Global'>;

    const computeChildrens = useMemo(
        () => currentPlatform?.editCategory?.childrens?.map((tag) => API.toLang(tag, i18n.language))  
    , [currentPlatform, i18n.language]) as Tag<'Local'>[];

    const findRawTag = useCallback((translatedTagId: any) => {
        return currentPlatform?.editCategory?.childrens?.find((tag) => tag.tagId === translatedTagId)
    }, [currentPlatform])

    const onCategoryTitleChange = (ev: any, lang: string) => {
        if (/^(|[a-zA-Zа-яА-ЯёЁ0-9][a-zA-Zа-яА-ЯёЁ0-9\s'-]*)$/.test(ev.target.value)) {
        //@ts-ignore
            setTitle(setTextByLang(title, lang, ev.target.value))
        }
    }

    const onSubCategoryChange = useCallback((ev: any, lang: string) => {
        if (/^(|[a-zA-Zа-яА-ЯёЁ0-9][a-zA-Zа-яА-ЯёЁ0-9\s'-]*)$/.test(ev.target.value)) {
            //@ts-ignore
            setSubTitle(setTextByLang(subTitle, lang, ev.target.value))
        }
    } , [subTitle])

    const onAddSubTag = () => {
        setType(SUB_TYPES.sub)
    }

    const handleCancelCreateClick = () => {
        setType(null);
        setSubTitle(createDefaultLangWithLocal());
    }

    const handleCancelCategoryClick = () => {
        setType(null);
        setIsEditTitle(false);
        setTitle(computeTag?.name as CurrentLangT[])
    }

    const handleCreateSubCategory = () => {
        if (subTitle?.some((i) => i?.name === '')) {
            toast.error(t('main_admin_tag.error.lang_error'));
            return;
        }
        setSubTitle(createDefaultLangWithLocal())
        onCreateSubCategory && onCreateSubCategory(subTitle)
        setType(null)
    }

    const handleCreateNewTag = (tag: any) => {
        onCreateNewTag({ tag, groupId: computeTag.tagId});
        setType(null);
    }

    const handleCategoryRename = () => {
        onCategoryRename && onCategoryRename({...computeTag, name: title })
        setIsEditTitle(false);
        setTime(Date.now())
    }

    useEffect(() => {
        if (computeTag) {
            //@ts-ignore
            setTitle(computeTag?.name?.map((obj) => ({...obj, currentLang: 'ru' })) as CurrentLangT[])
        }
    } , [computeTag, i18n])

      const MAX_LENGTH = useMemo(() => 75, [])
    
      const showError = useCallback(() => {
        toast.error(t('project_edit.max_title_length_error', { length: 75 }));
      }, [toast, t]);
    
      // @ts-ignore
      const onChange = useCallback((e, lang: string) => {
        const target = e.target as HTMLTextAreaElement;
        let value = target.value
    
        if (value.length >= 75) {
          showError();
          value.slice(0, 75)
        }
    
        onCategoryTitleChange(e, lang);
        autoResize(target);
      }, [onCategoryTitleChange, MAX_LENGTH]);
    
      useEffect(() => {
        if (textareaRef.current && !!textareaRef.current.scrollHeight) {
          const handler = () => {
            if (textareaRef.current) {
              autoResize(textareaRef.current);
            }
          };
    
          handler();
    
          window.addEventListener('resize', handler);
    
          return () => {
            window.removeEventListener('resize', handler);
          };
        }
      }, [
        textareaRef, 
        textareaRef.current, 
        title, 
        textareaRef.current?.scrollHeight
      ]);

    const Title = useMemo(() => {
        const currentLang = title?.find((i) => i?.currentLang)?.currentLang || 'ru';
        const disable = title?.some((i) => i?.name === '');

        return (
            <EditTitleContainer  direction="column" directionMobile="column" gy={16} offsetBottom={24} offsetBottomMobile={24}>
                {isEditTitle ? (
                    <CreateNewCatalogContainer style={{ border: 'none' }} direction="column" offsetTop={32} offsetTopMobile={32}>
                        <FlexBox offsetBottom={32} direction="column" gy={8} gyMobile={8}>
                            <span className="label">{t('main_admin_tag.category_edit_title')}</span>
                        </FlexBox>
        
                        <FlexBox gy={16} gyMobile={16} direction="column" directionMobile="column">
                            <CommonInput 
                                type={INPUT_TYPES.counter}
                                value={getLangTitle(title, 'ru')}
                                onChange={(ev: any) => onChange(ev, 'ru')}
                                maxLength={75}
                                orientation="column"
                                label={t('main_admin_tag.lang.subcategory.ru')}
                            /> 
                            <CommonInput 
                                type={INPUT_TYPES.counter}
                                value={getLangTitle(title, 'en')}
                                onChange={(ev: any) => onChange(ev, 'en')}
                                maxLength={75}
                                orientation="column"
                                label={t('main_admin_tag.lang.subcategory.ru')}
                            /> 
                        </FlexBox>
                        
                        <NewCategoryButtonsContainer offsetTop={24} offsetTopMobile={24} gx={24} direction="row">
                            <AddButton style={{ marginLeft: 0 }} onClick={handleCancelCategoryClick}>{t('main_admin_tag.buttons.cancel')}</AddButton>
                            <AddButton filled disable={disable} onClick={handleCategoryRename}>{t('main_admin_tag.buttons.add')}</AddButton>
                        </NewCategoryButtonsContainer>
        
                    </CreateNewCatalogContainer>
                ) : (
                    <FlexBox justify="space-between" align={'center'}>
                        <EditTextarea 
                            //@ts-ignore
                            ref={textareaRef}
                            rows={2}
                            isEdit={isEditTitle}
                            maxLength={75}
                            onDrop={() => false}
                            onChange={(ev: any) => onCategoryTitleChange(ev, currentLang)}
                            value={getLangTitle(title, i18n.language)}
                            disabled={!isEditTitle}
                            />
                        <div onClick={() => setIsEditTitle(!isEditTitle)}>
                            {isEditTitle ? (
                                <svg  width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M0.548618 0.404297C0.754347 0.198568 1.0879 0.198568 1.29363 0.404297L8.82311 7.93378L8.89357 7.86331L8.82311 7.93378C8.86999 7.98066 8.93358 8.007 8.99989 8.007C9.06619 8.007 9.12978 7.98066 9.17666 7.93378L9.1061 7.86321L9.17666 7.93378L16.7061 0.404298C16.9119 0.198568 17.2454 0.198568 17.4512 0.404298L17.5955 0.548619C17.8012 0.754348 17.8012 1.0879 17.5955 1.29363L10.066 8.82311L10.1367 8.89382L10.066 8.82311C10.0191 8.86999 9.99277 8.93358 9.99277 8.99989C9.99277 9.06619 10.0191 9.12978 10.066 9.17666L17.5955 16.7061C17.8024 16.9131 17.8007 17.2467 17.5951 17.4456C17.5268 17.5116 17.4893 17.5467 17.4677 17.566C17.2266 17.7827 16.893 17.7824 16.6919 17.5812L9.17666 10.066C9.07903 9.96836 8.92074 9.96836 8.82311 10.066L1.29364 17.5955C1.08667 17.8024 0.753017 17.8007 0.55421 17.5951C0.488131 17.5268 0.453119 17.4893 0.433765 17.4677C0.217026 17.2266 0.2174 16.893 0.41857 16.6919L7.93377 9.17666C7.98066 9.12978 8.007 9.06619 8.007 8.99989C8.007 8.93358 7.98066 8.86999 7.93378 8.82311L7.86306 8.89382L7.93378 8.82311L0.404297 1.29363C0.198568 1.0879 0.198568 0.754348 0.404297 0.548618L0.548618 0.404297Z" fill="#909090"/>
                                </svg>
                            ) : (
                                <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M17.2166 2.93934C17.8023 2.35355 18.7521 2.35355 19.3379 2.93934L21.4237 5.02513C22.0094 5.61091 22.0094 6.56066 21.4237 7.14645L7.33668 21.2334C7.13496 21.4352 6.8799 21.5752 6.60141 21.6371L3.21808 22.3889C2.47245 22.5546 1.82384 21.857 2.04333 21.1254L3.22586 17.1836C3.29719 16.9458 3.42641 16.7295 3.60194 16.554L17.2166 2.93934ZM18.6308 3.64645C18.4355 3.45118 18.1189 3.45118 17.9237 3.64645L15.9843 5.58579L18.7772 8.37868L20.7166 6.43934C20.9118 6.24408 20.9118 5.9275 20.7166 5.73223L18.6308 3.64645ZM18.0701 9.08579L15.2772 6.29289L4.30904 17.2611C4.25053 17.3196 4.20746 17.3917 4.18368 17.4709L3.00115 21.4127L6.38448 20.6609C6.47731 20.6402 6.56233 20.5936 6.62957 20.5263L18.0701 9.08579Z" fill="#909090"/>
                                </svg>
                            )}
                        </div>
                    </FlexBox>
                )}
        </EditTitleContainer>
        )
    } , [isEditTitle, onLocalLangChange, onCategoryTitleChange, title])

    const Buttons = useMemo(() => {
        if (isEditTitle) return null;

        return (
            <EditButtonsWrapper direction="row" gx={16}>
                <ProjectAddButton 
                    onClick={() => onChildrenAdd(computeChildrens)} 
                    reverse 
                    text={t('main_admin_tag.buttons.add_tag')}
                />
                <ProjectAddButton 
                    onClick={() => {
                        modal.show('createTag', {
                            isModal: true,
                            isEducation: tagType === ADMIN_TAGS_TYPE.education,
                            onCreateTag: handleCreateNewTag,
                            onClose: () => modal.hide()
                        })
                    }} 
                    reverse 
                    text={t('main_admin_tag.buttons.create_tag')}
                />
                <ProjectAddButton 
                    onClick={onAddSubTag} 
                    disable={type === SUB_TYPES.sub || computeTag?.level === TAGS_LEVEL.children} 
                    reverse 
                    text={t('main_admin_tag.buttons.create_catalog')}
                />
        </EditButtonsWrapper>
        )
    }, [computeChildrens, isEditTitle, tagType, onChildrenAdd, type, computeTag])

    const Inputs = useMemo(() => {
        if (type !== SUB_TYPES.sub) {
            return null;
        }

        const disable = subTitle?.some((i: any) => i?.name === '') || computeTag?.level === TAGS_LEVEL.children

        return (
            <CreateNewCatalogContainer gy={24} direction="column" offsetTop={32} offsetTopMobile={32}>
                <FlexBox direction="column" gy={8} gyMobile={8}>
                    <span className="label">{t('main_admin_tag.catalog_create_title')}</span>
                    <span className="description">
                    {t('main_admin_tag.catalog_create_desk')}
                    </span>
                </FlexBox>

                <FlexBox gy={16} gyMobile={16} direction="column" directionMobile="column">
                    <CommonInput 
                        type={INPUT_TYPES.counter}
                        value={getLangTitle(subTitle, 'ru')}
                        onChange={(ev: any) => onSubCategoryChange(ev, 'ru')}
                        maxLength={75}
                        orientation="column"
                        label={t('main_admin_tag.lang.subcategory.ru')}
                    /> 
                    <CommonInput 
                        type={INPUT_TYPES.counter}
                        value={getLangTitle(subTitle, 'en')}
                        onChange={(ev: any) => onSubCategoryChange(ev, 'en')}
                        maxLength={75}
                        orientation="column"
                        label={t('main_admin_tag.lang.subcategory.en')}
                    /> 
                </FlexBox>
               
                <NewCategoryButtonsContainer gx={24} direction="row">
                    <AddButton style={{ marginLeft: 0 }} onClick={handleCancelCreateClick}>{t('main_admin_tag.buttons.cancel')}</AddButton>
                    <AddButton filled disable={disable} onClick={handleCreateSubCategory}>{t('main_admin_tag.buttons.add')}</AddButton>
                </NewCategoryButtonsContainer>

            </CreateNewCatalogContainer>
        )
    } , [subTitle, type, computeTag, isEditTitle, handleCreateSubCategory, onLocalLangChange, onSubCategoryChange])

    const Tags = useMemo(() => {

        return (
            <FlexBox direction="column" offsetTop={56} gy={24} gyMobile={24}>
                <span className="label">{t('main_admin_tag.tags_title')}</span>

                <BTagsGridNew scrollMode="all" style={{ minHeight: '655px', maxHeight: '655px' }}>
                    {computeChildrens?.map((tag) => {
                        const rawTag = findRawTag(tag.tagId);
                        return (
                            <BTag
                                key={tag.tagId}
                                tag={tag}
                                removable
                                showRemoveIcon
                                educational={tag.isEducationTag}
                                organization={tag.isTeamTag}
                                isProjectsTag
                                onIconClick={() => onChildrenRemove(tag.tagId)}
                                editIconClick={onTagEdit ? () => onTagEdit(rawTag) : undefined}
                            >
                                {bTag.title(tag)}
                        </BTag>
                        )
                    })}
                </BTagsGridNew>
            </FlexBox>
        )
    }, [computeChildrens, onChildrenRemove, isEditTitle, type, onTagEdit, findRawTag])

    if (!isEditMode) {
        return null;
    }


    return (
        <ColumnContainer>
            {Title}
            {Buttons}
            {Inputs}
            {Tags}
        </ColumnContainer>
    )
}import { API, Tag } from "@brg/api";
import { CommonInput, FlexBox, INPUT_TYPES, useToasts } from "@brg/ui";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { TagAdminSelector } from "shared/reducers/tagAdminSlice";
import { AddButton, ColumnContainer, CreateNewCatalogContainer, EditButtonsWrapper, EditTextarea, EditTitleContainer, NewCategoryButtonsContainer, SmallLabel } from "../styles";
import { ProjectAddButton } from "shared/pages/projectEdit/ProjectAddButton";
import { Input } from "@brg/input";
import { BTag } from "core/components";
import { bTag, useModal } from "core";
import { LangObject } from "@brg/api/dist/api-core/utils/lang-utils";
import { setTextByLang } from "shared/pages/teams/TeamEdit";
import { createDefaultLangWithLocal, getLangTitle } from "../MainTagAdminPage";
import { TAGS_LEVEL } from "shared/modals/selectTagsModal/FoundedChildrens";
import {BTagsGridNew} from "../../../../../core/components/bTag/bTagsGridNew/bTagsGridNew";
import { ADMIN_TAGS_TYPE } from "../types";

//при стандартном значение lineHeight режутся некоторые буквы
const ADDITIONAL_HEIGHT_PART = 3;

export interface CurrentLangT extends LangObject {
    currentLang: string;
}

interface InewTagProps {
    tag: Tag<'Local'>,
    groupId: string
}

export enum SUB_TYPES {
    sub = 'sub',
    add = 'add'
}

const autoResize = (area: HTMLTextAreaElement) => {
    const lineHeight = window.getComputedStyle(area as HTMLElement, null).getPropertyValue('line-height');
    area.style.height = 'auto';
    area.rows = 1
    const secondLine = area.scrollHeight >= (parseInt(lineHeight) * 2);
  
    if (secondLine) {
      area.rows = 2
      area.style.height = area.scrollHeight + 'px';
    } else {
      area.style.height = `${parseInt(lineHeight) + ADDITIONAL_HEIGHT_PART}px`;
    }
  };

export const CategoryEdit: FC<{
    onChildrenRemove: (ev: any) => void;
    onChildrenAdd: (tags: Tag<'Local'>[]) => void;
    onCreateSubCategory: (name: CurrentLangT[]) => void;
    onCreateNewTag: ({ tag, groupId}: InewTagProps) => void;
    onCategoryRename: (tag: Tag<'Global'>) => void;
    onLocalLangChange: (arr: any[], lang: string, callback: () => any[]) => void;
    update?: number;
    onTagEdit?: (tagData: any) => void
}> = ({
    onChildrenRemove,
    onChildrenAdd,
    onCategoryRename,
    onCreateSubCategory,
    onCreateNewTag,
    onLocalLangChange,
    update,
    onTagEdit
}) => {
    const { t, i18n } = useTranslation();
    const toast = useToasts();
    const modal = useModal();
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const { isEditMode, currentPlatform, isLoading, type: tagType } = useSelector(TagAdminSelector);
    const [isEditTitle, setIsEditTitle ] = useState(false);
    const [time, setTime] = useState(Date.now())
    const [title, setTitle] = useState<CurrentLangT[]>(createDefaultLangWithLocal());
    const [subTitle, setSubTitle] = useState<CurrentLangT[]>(createDefaultLangWithLocal());
    const [type, setType] = useState<SUB_TYPES | null>();

    const computeTag = useMemo(
        //@ts-ignore
        () => currentPlatform?.editCategory?.mainTag as Tag<'Global'>
    , [currentPlatform, update, title]) as Tag<'Global'>;

    const computeChildrens = useMemo(
        () => currentPlatform?.editCategory?.childrens?.map((tag) => API.toLang(tag, i18n.language))  
    , [currentPlatform, i18n.language]) as Tag<'Local'>[];

    const findRawTag = useCallback((translatedTagId: any) => {
        return currentPlatform?.editCategory?.childrens?.find((tag) => tag.tagId === translatedTagId)
    }, [currentPlatform])

    const onCategoryTitleChange = (ev: any, lang: string) => {
        if (/^(|[a-zA-Zа-яА-ЯёЁ0-9][a-zA-Zа-яА-ЯёЁ0-9\s'-]*)$/.test(ev.target.value)) {
        //@ts-ignore
            setTitle(setTextByLang(title, lang, ev.target.value))
        }
    }

    const onSubCategoryChange = useCallback((ev: any, lang: string) => {
        if (/^(|[a-zA-Zа-яА-ЯёЁ0-9][a-zA-Zа-яА-ЯёЁ0-9\s'-]*)$/.test(ev.target.value)) {
            //@ts-ignore
            setSubTitle(setTextByLang(subTitle, lang, ev.target.value))
        }
    } , [subTitle])

    const onAddSubTag = () => {
        setType(SUB_TYPES.sub)
    }

    const handleCancelCreateClick = () => {
        setType(null);
        setSubTitle(createDefaultLangWithLocal());
    }

    const handleCancelCategoryClick = () => {
        setType(null);
        setIsEditTitle(false);
        setTitle(computeTag?.name as CurrentLangT[])
    }

    const handleCreateSubCategory = () => {
        if (subTitle?.some((i) => i?.name === '')) {
            toast.error(t('main_admin_tag.error.lang_error'));
            return;
        }
        setSubTitle(createDefaultLangWithLocal())
        onCreateSubCategory && onCreateSubCategory(subTitle)
        setType(null)
    }

    const handleCreateNewTag = (tag: any) => {
        onCreateNewTag({ tag, groupId: computeTag.tagId});
        setType(null);
    }

    const handleCategoryRename = () => {
        onCategoryRename && onCategoryRename({...computeTag, name: title })
        setIsEditTitle(false);
        setTime(Date.now())
    }

    useEffect(() => {
        if (computeTag) {
            //@ts-ignore
            setTitle(computeTag?.name?.map((obj) => ({...obj, currentLang: 'ru' })) as CurrentLangT[])
        }
    } , [computeTag, i18n])

      const MAX_LENGTH = useMemo(() => 75, [])
    
      const showError = useCallback(() => {
        toast.error(t('project_edit.max_title_length_error', { length: 75 }));
      }, [toast, t]);
    
      // @ts-ignore
      const onChange = useCallback((e, lang: string) => {
        const target = e.target as HTMLTextAreaElement;
        let value = target.value
    
        if (value.length >= 75) {
          showError();
          value.slice(0, 75)
        }
    
        onCategoryTitleChange(e, lang);
        autoResize(target);
      }, [onCategoryTitleChange, MAX_LENGTH]);
    
      useEffect(() => {
        if (textareaRef.current && !!textareaRef.current.scrollHeight) {
          const handler = () => {
            if (textareaRef.current) {
              autoResize(textareaRef.current);
            }
          };
    
          handler();
    
          window.addEventListener('resize', handler);
    
          return () => {
            window.removeEventListener('resize', handler);
          };
        }
      }, [
        textareaRef, 
        textareaRef.current, 
        title, 
        textareaRef.current?.scrollHeight
      ]);

    const Title = useMemo(() => {
        const currentLang = title?.find((i) => i?.currentLang)?.currentLang || 'ru';
        const disable = title?.some((i) => i?.name === '');

        return (
            <EditTitleContainer  direction="column" directionMobile="column" gy={16} offsetBottom={24} offsetBottomMobile={24}>
                {isEditTitle ? (
                    <CreateNewCatalogContainer style={{ border: 'none' }} direction="column" offsetTop={32} offsetTopMobile={32}>
                        <FlexBox offsetBottom={32} direction="column" gy={8} gyMobile={8}>
                            <span className="label">{t('main_admin_tag.category_edit_title')}</span>
                        </FlexBox>
        
                        <FlexBox gy={16} gyMobile={16} direction="column" directionMobile="column">
                            <CommonInput 
                                type={INPUT_TYPES.counter}
                                value={getLangTitle(title, 'ru')}
                                onChange={(ev: any) => onChange(ev, 'ru')}
                                maxLength={75}
                                orientation="column"
                                label={t('main_admin_tag.lang.subcategory.ru')}
                            /> 
                            <CommonInput 
                                type={INPUT_TYPES.counter}
                                value={getLangTitle(title, 'en')}
                                onChange={(ev: any) => onChange(ev, 'en')}
                                maxLength={75}
                                orientation="column"
                                label={t('main_admin_tag.lang.subcategory.ru')}
                            /> 
                        </FlexBox>
                        
                        <NewCategoryButtonsContainer offsetTop={24} offsetTopMobile={24} gx={24} direction="row">
                            <AddButton style={{ marginLeft: 0 }} onClick={handleCancelCategoryClick}>{t('main_admin_tag.buttons.cancel')}</AddButton>
                            <AddButton filled disable={disable} onClick={handleCategoryRename}>{t('main_admin_tag.buttons.add')}</AddButton>
                        </NewCategoryButtonsContainer>
        
                    </CreateNewCatalogContainer>
                ) : (
                    <FlexBox justify="space-between" align={'center'}>
                        <EditTextarea 
                            //@ts-ignore
                            ref={textareaRef}
                            rows={2}
                            isEdit={isEditTitle}
                            maxLength={75}
                            onDrop={() => false}
                            onChange={(ev: any) => onCategoryTitleChange(ev, currentLang)}
                            value={getLangTitle(title, i18n.language)}
                            disabled={!isEditTitle}
                            />
                        <div onClick={() => setIsEditTitle(!isEditTitle)}>
                            {isEditTitle ? (
                                <svg  width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M0.548618 0.404297C0.754347 0.198568 1.0879 0.198568 1.29363 0.404297L8.82311 7.93378L8.89357 7.86331L8.82311 7.93378C8.86999 7.98066 8.93358 8.007 8.99989 8.007C9.06619 8.007 9.12978 7.98066 9.17666 7.93378L9.1061 7.86321L9.17666 7.93378L16.7061 0.404298C16.9119 0.198568 17.2454 0.198568 17.4512 0.404298L17.5955 0.548619C17.8012 0.754348 17.8012 1.0879 17.5955 1.29363L10.066 8.82311L10.1367 8.89382L10.066 8.82311C10.0191 8.86999 9.99277 8.93358 9.99277 8.99989C9.99277 9.06619 10.0191 9.12978 10.066 9.17666L17.5955 16.7061C17.8024 16.9131 17.8007 17.2467 17.5951 17.4456C17.5268 17.5116 17.4893 17.5467 17.4677 17.566C17.2266 17.7827 16.893 17.7824 16.6919 17.5812L9.17666 10.066C9.07903 9.96836 8.92074 9.96836 8.82311 10.066L1.29364 17.5955C1.08667 17.8024 0.753017 17.8007 0.55421 17.5951C0.488131 17.5268 0.453119 17.4893 0.433765 17.4677C0.217026 17.2266 0.2174 16.893 0.41857 16.6919L7.93377 9.17666C7.98066 9.12978 8.007 9.06619 8.007 8.99989C8.007 8.93358 7.98066 8.86999 7.93378 8.82311L7.86306 8.89382L7.93378 8.82311L0.404297 1.29363C0.198568 1.0879 0.198568 0.754348 0.404297 0.548618L0.548618 0.404297Z" fill="#909090"/>
                                </svg>
                            ) : (
                                <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M17.2166 2.93934C17.8023 2.35355 18.7521 2.35355 19.3379 2.93934L21.4237 5.02513C22.0094 5.61091 22.0094 6.56066 21.4237 7.14645L7.33668 21.2334C7.13496 21.4352 6.8799 21.5752 6.60141 21.6371L3.21808 22.3889C2.47245 22.5546 1.82384 21.857 2.04333 21.1254L3.22586 17.1836C3.29719 16.9458 3.42641 16.7295 3.60194 16.554L17.2166 2.93934ZM18.6308 3.64645C18.4355 3.45118 18.1189 3.45118 17.9237 3.64645L15.9843 5.58579L18.7772 8.37868L20.7166 6.43934C20.9118 6.24408 20.9118 5.9275 20.7166 5.73223L18.6308 3.64645ZM18.0701 9.08579L15.2772 6.29289L4.30904 17.2611C4.25053 17.3196 4.20746 17.3917 4.18368 17.4709L3.00115 21.4127L6.38448 20.6609C6.47731 20.6402 6.56233 20.5936 6.62957 20.5263L18.0701 9.08579Z" fill="#909090"/>
                                </svg>
                            )}
                        </div>
                    </FlexBox>
                )}
        </EditTitleContainer>
        )
    } , [isEditTitle, onLocalLangChange, onCategoryTitleChange, title])

    const Buttons = useMemo(() => {
        if (isEditTitle) return null;

        return (
            <EditButtonsWrapper direction="row" gx={16}>
                <ProjectAddButton 
                    onClick={() => onChildrenAdd(computeChildrens)} 
                    reverse 
                    text={t('main_admin_tag.buttons.add_tag')}
                />
                <ProjectAddButton 
                    onClick={() => {
                        modal.show('createTag', {
                            isModal: true,
                            isEducation: tagType === ADMIN_TAGS_TYPE.education,
                            onCreateTag: handleCreateNewTag,
                            onClose: () => modal.hide()
                        })
                    }} 
                    reverse 
                    text={t('main_admin_tag.buttons.create_tag')}
                />
                <ProjectAddButton 
                    onClick={onAddSubTag} 
                    disable={type === SUB_TYPES.sub || computeTag?.level === TAGS_LEVEL.children} 
                    reverse 
                    text={t('main_admin_tag.buttons.create_catalog')}
                />
        </EditButtonsWrapper>
        )
    }, [computeChildrens, isEditTitle, tagType, onChildrenAdd, type, computeTag])

    const Inputs = useMemo(() => {
        if (type !== SUB_TYPES.sub) {
            return null;
        }

        const disable = subTitle?.some((i: any) => i?.name === '') || computeTag?.level === TAGS_LEVEL.children

        return (
            <CreateNewCatalogContainer gy={24} direction="column" offsetTop={32} offsetTopMobile={32}>
                <FlexBox direction="column" gy={8} gyMobile={8}>
                    <span className="label">{t('main_admin_tag.catalog_create_title')}</span>
                    <span className="description">
                    {t('main_admin_tag.catalog_create_desk')}
                    </span>
                </FlexBox>

                <FlexBox gy={16} gyMobile={16} direction="column" directionMobile="column">
                    <CommonInput 
                        type={INPUT_TYPES.counter}
                        value={getLangTitle(subTitle, 'ru')}
                        onChange={(ev: any) => onSubCategoryChange(ev, 'ru')}
                        maxLength={75}
                        orientation="column"
                        label={t('main_admin_tag.lang.subcategory.ru')}
                    /> 
                    <CommonInput 
                        type={INPUT_TYPES.counter}
                        value={getLangTitle(subTitle, 'en')}
                        onChange={(ev: any) => onSubCategoryChange(ev, 'en')}
                        maxLength={75}
                        orientation="column"
                        label={t('main_admin_tag.lang.subcategory.en')}
                    /> 
                </FlexBox>
               
                <NewCategoryButtonsContainer gx={24} direction="row">
                    <AddButton style={{ marginLeft: 0 }} onClick={handleCancelCreateClick}>{t('main_admin_tag.buttons.cancel')}</AddButton>
                    <AddButton filled disable={disable} onClick={handleCreateSubCategory}>{t('main_admin_tag.buttons.add')}</AddButton>
                </NewCategoryButtonsContainer>

            </CreateNewCatalogContainer>
        )
    } , [subTitle, type, computeTag, isEditTitle, handleCreateSubCategory, onLocalLangChange, onSubCategoryChange])

    const Tags = useMemo(() => {

        return (
            <FlexBox direction="column" offsetTop={56} gy={24} gyMobile={24}>
                <span className="label">{t('main_admin_tag.tags_title')}</span>

                <BTagsGridNew scrollMode="all" style={{ minHeight: '655px', maxHeight: '655px' }}>
                    {computeChildrens?.map((tag) => {
                        const rawTag = findRawTag(tag.tagId);
                        return (
                            <BTag
                                key={tag.tagId}
                                tag={tag}
                                removable
                                showRemoveIcon
                                educational={tag.isEducationTag}
                                organization={tag.isTeamTag}
                                isProjectsTag
                                onIconClick={() => onChildrenRemove(tag.tagId)}
                                editIconClick={onTagEdit ? () => onTagEdit(rawTag) : undefined}
                            >
                                {bTag.title(tag)}
                        </BTag>
                        )
                    })}
                </BTagsGridNew>
            </FlexBox>
        )
    }, [computeChildrens, onChildrenRemove, isEditTitle, type, onTagEdit, findRawTag])

    if (!isEditMode) {
        return null;
    }


    return (
        <ColumnContainer>
            {Title}
            {Buttons}
            {Inputs}
            {Tags}
        </ColumnContainer>
    )
}